import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { ClientService } from '../../services/client.service';
import { RecordService } from '../../services/record.service';
import { User } from '../../shared/models/user.model';
import { Client } from '../../shared/models/client.model';
import { Record } from '../../shared/models/record.model';
import { ToastComponent } from '../../shared/toast/toast.component';
import { ee, perPage50 } from '../../shared/constants';
import { removeTooltipElement } from '../../shared/functions';
import { exportChart, generateBarChart } from '../../shared/chartFunctions';
import { BarChartParams } from '../../shared/types';
import { finalize } from 'rxjs/operators';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
  standalone: false
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  auth = inject(AuthService);
  private userService = inject(UserService);
  private clientService = inject(ClientService);
  private recordService = inject(RecordService);
  toast = inject(ToastComponent);
  private route = inject(ActivatedRoute);
  private router = inject(Router);


  perPage = perPage50;
  exportChart = exportChart;
  isLoading = {
    user: true,
    records: true,
    stats: true
  };
  id = '';
  user: User;
  clients: Client[] = [];
  records: Record[] = [];
  count = 0;
  page = 0;
  canViewTeamClients = false;
  // Stats
  chartParams: BarChartParams = {
    label: 'activities',
  };
  counters = { months: [], avg: 0 };
  barChartWeek;
  barChartActivity;
  currentContent = 'track-record';
  menuItems = [
    { name: 'Info & Records', link: 'track-record', icon: 'list' },
    { name: 'Statistics', link: 'stats', icon: 'bar-chart' }
  ];

  ngOnInit(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.id = this.route.snapshot.paramMap.get('id');
    this.canViewTeamClients = this.auth.currentUser.permissions.includes('viewTeamClients');
    ee.on('changeContent', (data: string) => this.currentContent = data);
    ee.on('getRecords', (page?: number) => this.getRecords(this.id, page));
    this.getUser(this.id);
    this.getClients();
    this.getRecords(this.id);
    this.getStats(this.id);
  }

  ngOnDestroy(): void {
    ee.off('changeContent', () => {});
    ee.off('getRecords', () => {});
    removeTooltipElement();
    this.barChartWeek?.destroy();
    this.barChartActivity?.destroy();
  }

  getUser(id: string): void {
    this.isLoading.user = true;
    this.userService.get(id)
      .pipe(finalize(() => this.isLoading.user = false))
      .subscribe({
        next: res => this.user = res,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getClients(): void {
    this.clientService.search({ active: '1' })
      .subscribe({
        next: res => this.clients = res.results,
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getRecords(id: string, page?: number): void {
    this.isLoading.records = true;
    this.page = page || 0;
    this.recordService.search({
      _user: id,
      _users: id,
      page: this.page,
      perPage: this.perPage,
    })
      .pipe(finalize(() => this.isLoading.records = false))
      .subscribe({
        next: res => {
          this.records = res.results;
          this.count = res.count;
        },
        error: err => this.toast.setMessage(err.error.message, 'danger'),
      });
  }

  getStats(id: string): void {
    forkJoin([
      this.recordService.getStatsCounters({ model: 'user', id }),
      this.recordService.getStatsByWeek({ model: 'user', id }),
      this.recordService.getStatsByActivity({ model: 'user', id })
    ])
      .subscribe({
        next: ([statsCounters, statsByWeek, statsByActivity]) => {
          this.counters = statsCounters;
          this.barChartWeek = generateBarChart(statsByWeek, '1', { ...this.chartParams, verticalBars: true });
          this.barChartActivity = generateBarChart(statsByActivity, '2', this.chartParams);
          this.isLoading.stats = false;
        },
        error: err => this.toast.setMessage(err.error.message, 'danger')
      });
  }

}
